import { useInfiniteQuery } from '@tanstack/react-query';
import { get } from '../api/api';
import { API_URLS } from '../constants/apiUrl';
import { QueryParamObject, toQueryString } from '../constants/utilityFunctions';
interface IUseFetchNotes {
  relations?: string[];
  sortBy?: string[];
  extraFilters?: Record<string, string | number>;
  pageSize?: number;
  search?: string;
  fields?: string[];
}
interface INotesConfig {
  enabled?: boolean;
}
export const useFetchNotes = (params: IUseFetchNotes, config?: INotesConfig) => {
  const fetchNotes = async ({ pageParam = 1 }) => {
    const { relations: noteRelations, extraFilters: filters, ...rest } = params;
    const queryString = new URLSearchParams(
      Object.keys(rest)?.length ? toQueryString(rest as QueryParamObject) : '',
    );

    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (value) queryString.append(`filter.${key}`, String(value));
    });

    (noteRelations ?? []).forEach((rel) => queryString.append('relations', rel));

    queryString.append('page', String(pageParam));
    queryString.append('limit', String(params.pageSize));
    try {
      return await get(`${API_URLS.NOTES_LIST}?${queryString.toString()}`);
    } catch (error) {
      console.error('Error fetching Notes:', error);
      throw new Error((error as { message: string }).message || 'Failed to fetch notes'); // Force error throwing
    }
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['notes-list', params],
    queryFn: fetchNotes,
    enabled: config?.enabled ?? false,
    getNextPageParam: (lastPage, allPages) => {
      const totalCount = lastPage?.meta?.totalItems ?? 0;
      const fetchedCount = allPages.flatMap((page) => page.data).length;
      return fetchedCount < totalCount ? allPages.length + 1 : undefined;
    },
  });
  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  };
};
